import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// ── Decoration ─────────────────────────────────────────────────────────────

// The couple's initials as a typographic lockup — the anchor for the
// editorial style and the opening reveal.
export function Monogram({ className = '', size = 'text-5xl' }) {
  return (
    <span
      className={`font-display font-bold tracking-tight bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent ${size} ${className}`}
    >
      Y<span className="font-accent font-normal px-1.5 opacity-70">&amp;</span>L
    </span>
  );
}

// A pair of hairlines around a diamond — the house divider, used here by the
// opening reveal.
export function Flourish({ className = '' }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden="true">
      <span className="h-px w-14 bg-gradient-to-r from-transparent to-secondary/50" />
      <span className="text-secondary text-[10px] leading-none">◆</span>
      <span className="h-px w-14 bg-gradient-to-l from-transparent to-secondary/50" />
    </div>
  );
}

// ── Opening reveal ─────────────────────────────────────────────────────────

const REVEAL_KEY = 'weddingRevealSeen';

// A brief monogram card that lifts away on first arrival. Runs once per
// browser session — a reveal you sit through on every navigation stops being
// charming very quickly. Skipped entirely for reduced-motion users.
export function OpeningReveal({ isDarkMode }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    let seen = false;
    try {
      seen = sessionStorage.getItem(REVEAL_KEY) === '1';
    } catch {
      /* storage unavailable — treat as unseen */
    }
    if (seen) return;
    setVisible(true);
    try {
      sessionStorage.setItem(REVEAL_KEY, '1');
    } catch {
      /* storage unavailable — it just replays next load */
    }
    const timer = setTimeout(() => setVisible(false), 1750);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 z-[90] flex items-center justify-center ${
        isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
      }`}
      // Purely decorative and self-dismissing — never trap focus or a tap.
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.94, 1, 1, 1.04] }}
        transition={{ duration: 1.75, times: [0, 0.28, 0.72, 1], ease: [0.25, 1, 0.5, 1] }}
        className="flex flex-col items-center gap-4"
      >
        <Monogram size="text-6xl" />
        <Flourish />
      </motion.div>
    </motion.div>
  );
}
